import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { SafetyTest } from '../data-models/safety-test.model';
import { TestAttempt } from '../data-models/test-attempt.model';
import { TestQuestion } from '../data-models/test-question.model';
import { SafetyTestCreateRequest } from '../request-models/safety-test-create-request.model';
import { SafetyTestRequest } from '../request-models/safety-test-request.model';
import { TestQuestionCreateRequest } from '../request-models/test-question-create-request.model';
import { QuestionImportPreview } from '../response-models/question-import-preview.model';
import { QuestionImportResult } from '../response-models/question-import-result.model';
import { SafetyTestWithQuestions } from '../response-models/safety-test-with-questions.model';
import { TestAnalytics } from '../response-models/test-analytics.model';
import { isRecord } from '../utils/is-record';
import { toSafetyTestCreateRequest, toSafetyTestRequest } from '../utils/safety-test-request-utils';
import { toQuestionImportPreview, toTestAnalytics } from '../utils/safety-test-wire-utils';
import { countOrZero, listOf } from '../utils/wire-value-utils';

/** Filters for `GET /admin/safety-tests`. The hub applies `categoryId` before `organizationId`. */
export interface SafetyTestListFilters {
  categoryId?: string;
  status?: string;
  organizationId?: string;
}

/**
 * Safety tests: `/admin/safety-tests` (hub `admin-safety-tests-controller.ts`), with versions, study guide,
 * analytics, CSV import / export and the org attempts list (`/admin/safety-tests/test-attempts`). Questions have
 * `TestQuestionService`, categories `SafetyTestCategoryService` and certificates `TestAttemptService`. Not paged.
 * Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class SafetyTestService extends BaseService<SafetyTest> {
  constructor() {
    super('admin/safety-tests', SafetyTest);
  }

  /** GET /admin/safety-tests, each with its `questionCount`. Blank filters are not sent. */
  listTests(filters: SafetyTestListFilters = {}): Promise<SafetyTest[]> {
    const query = {
      categoryId: filters.categoryId || undefined,
      status: filters.status || undefined,
      organizationId: filters.organizationId || undefined,
    };
    return firstValueFrom(this.getMany({ query }));
  }

  /** Whether any test uses the category (GET /admin/safety-tests?categoryId=). */
  async hasTestsInCategory(categoryId: string): Promise<boolean> {
    const tests = await this.listTests({ categoryId });
    return tests.length > 0;
  }

  /** GET /admin/safety-tests/:id with its questions and answers. */
  getTest(testId: string): Promise<SafetyTestWithQuestions | null> {
    return firstValueFrom(this.request('GET', { id: testId }, toTestWithQuestions));
  }

  /** POST /admin/safety-tests: creates a draft test with its initial questions. */
  createTest(data: SafetyTestCreateRequest, questions?: TestQuestionCreateRequest[]): Promise<SafetyTest | null> {
    return firstValueFrom(this.create(toSafetyTestCreateRequest({ ...data, questions })));
  }

  /**
   * PUT /admin/safety-tests/:id with only the writable fields that are set. The hub refuses passing score,
   * time limit and question count changes on an active test (a new version is needed).
   */
  updateTest(testId: string, data: SafetyTestRequest): Promise<SafetyTest | null> {
    return firstValueFrom(this.update(testId, toSafetyTestRequest(data)));
  }

  /** DELETE /admin/safety-tests/:id (drafts only). */
  deleteTest(testId: string): Promise<void> {
    return firstValueFrom(this.delete(testId));
  }

  /** POST /admin/safety-tests/:id/activate (drafts only). */
  activateTest(testId: string): Promise<SafetyTest | null> {
    return firstValueFrom(this.request('POST', { id: testId, path: 'activate' }, toTestOrNull, {}));
  }

  /** POST /admin/safety-tests/:id/new-version: a draft copy (with questions) carrying `data`'s changes. */
  createNewVersion(testId: string, data: SafetyTestRequest = {}): Promise<SafetyTest | null> {
    return firstValueFrom(this.request('POST', { id: testId, path: 'new-version' }, toTestOrNull, toSafetyTestRequest(data)));
  }

  /** GET /admin/safety-tests/:id/versions: every version of the test, newest first. */
  getVersionHistory(testId: string): Promise<SafetyTest[]> {
    return firstValueFrom(this.request('GET', { id: testId, path: 'versions' }, toTests));
  }

  /** POST /admin/safety-tests/:id/study-guide (multipart PDF). */
  uploadStudyGuide(testId: string, file: File): Promise<SafetyTest | null> {
    const formData = new FormData();
    formData.append('file', file);
    return firstValueFrom(this.request('POST', { id: testId, path: 'study-guide' }, toTestOrNull, formData));
  }

  /** DELETE /admin/safety-tests/:id/study-guide */
  deleteStudyGuide(testId: string): Promise<SafetyTest | null> {
    return firstValueFrom(this.request('DELETE', { id: testId, path: 'study-guide' }, toTestOrNull));
  }

  /** GET /admin/safety-tests/test-attempts?organizationId=: an organization's attempts with test titles. */
  listAttempts(organizationId: string, status?: string): Promise<TestAttempt[]> {
    const query = { organizationId, status: status || undefined };
    return firstValueFrom(this.request('GET', { path: 'test-attempts', query }, toAttempts));
  }

  /** GET /admin/safety-tests/analytics, optionally limited to `range` (`7d`, `30d`, `90d`). */
  getAnalytics(range?: string): Promise<TestAnalytics> {
    return firstValueFrom(this.request('GET', { path: 'analytics', query: { range: range || undefined } }, toTestAnalytics));
  }

  /** GET /admin/safety-tests/export/results as a CSV `Blob`, optionally for one test. */
  exportResults(testId?: string): Promise<Blob> {
    return firstValueFrom(this.download({ path: ['export', 'results'], query: { testId: testId || undefined } }));
  }

  /** GET /admin/safety-tests/:id/export/questions as a CSV `Blob`. */
  exportQuestions(testId: string): Promise<Blob> {
    return firstValueFrom(this.download({ id: testId, path: ['export', 'questions'] }));
  }

  /** POST /admin/safety-tests/:id/import/questions with `preview`: parses the CSV without saving. */
  previewImportQuestions(testId: string, csvData: string): Promise<QuestionImportPreview | null> {
    const body = { csvData, preview: true };
    return firstValueFrom(this.request('POST', { id: testId, path: ['import', 'questions'] }, toQuestionImportPreview, body));
  }

  /**
   * POST /admin/safety-tests/:id/import/questions: saves the CSV's valid rows (replacing every question when
   * `replace`). Rejects with `NO_VALID_QUESTIONS` when no row is valid; read the rows with `noValidQuestionRows`.
   */
  importQuestions(testId: string, csvData: string, replace = false): Promise<QuestionImportResult> {
    const body = { csvData, preview: false, replace };
    return firstValueFrom(this.request('POST', { id: testId, path: ['import', 'questions'] }, toImportResult, body));
  }
}

function toTestOrNull(data: unknown): SafetyTest | null {
  return data === null || data === undefined ? null : new SafetyTest(data);
}

function toTests(data: unknown): SafetyTest[] {
  return listOf(data, item => new SafetyTest(item));
}

function toAttempts(data: unknown): TestAttempt[] {
  return listOf(data, item => new TestAttempt(item));
}

function toTestWithQuestions(data: unknown): SafetyTestWithQuestions | null {
  if (!isRecord(data)) {
    return null;
  }
  return Object.assign(new SafetyTest(data), { questions: listOf(data['questions'], item => new TestQuestion(item)) });
}

function toImportResult(data: unknown): QuestionImportResult {
  const source = isRecord(data) ? data : {};
  return {
    imported: countOrZero(source['imported']),
    skipped: countOrZero(source['skipped']),
    replaced: countOrZero(source['replaced']),
    questions: listOf(source['questions'], item => new TestQuestion(item)),
  };
}
